import {
  Coordinate,
  CreateDENMPayload,
  DisableDENMPayload,
  GetDENMListPaylaod,
  GetDENMPayload,
  Method,
  UpdateDENMPayload,
} from '_lib/api';
import fetchAPI from '_lib/api/fetchWrapper';
import apiRoute from '_lib/api/paths';
import { PointSelectionType, ToastTitle, TopologyType } from '_store/application/types';
import { getIsoDate } from '_store/utils';
import { PayloadAction } from '@reduxjs/toolkit';
import { differenceInMilliseconds } from 'date-fns';
import { createSliceSaga,SagaType } from 'redux-toolkit-saga';
import { delay, put, select } from 'typed-redux-saga';

import { getTopologyType } from '../application/hooks';
import appReducer from '../application/reducer';
import { selectedRoadSegmentIdsSelector } from '../roads/selectors';
import denmsReducer from './denmsReducers';
import { DenmSourceFilter, DenmStatusFilter } from './denmsTypes';
import { denmsFilteredSelector,denmsSelectorForSelectedRoadSegment } from './selectors';

const pageLimit = 100;

const getDenmDate = (date: string | null) => {
  if (date) {
    return getIsoDate(date);
  }
  const lastDay = new Date(Date.now() - 24 * 60 * 60 * 1000);
  return lastDay.toISOString();
};

interface DENMListPayloadProps {
  date: string;
  sources: DenmSourceFilter[];
  status: DenmStatusFilter | '';
  roadSegmentIds?: string[];
  causeId?: string;
  subCauseId?: string;
  updatedFrom?: Date;
  updatedTo?: Date;
  sincroDispatchable?: boolean;
  alwaysShowActiveEvents?: boolean;
  excludeCauseAndSubcause?: boolean;
  page?: number;
  limit?: number;
}

const getDenmRequestPayload = ({
  date,
  sources,
  status,
  roadSegmentIds,
  causeId,
  subCauseId,
  updatedFrom,
  updatedTo,
  sincroDispatchable,
  excludeCauseAndSubcause,
  page,
  limit,
}: DENMListPayloadProps) => {
  if (!sources || sources.length === 0) return;

  const dateString = getDenmDate(date);
  const topologyType = getTopologyType();

  const roadSegments =
    topologyType === TopologyType.URBAN
      ? ''
      : roadSegmentIds && roadSegmentIds.length > 0 && sources.includes(DenmSourceFilter.EMERAS)
      ? roadSegmentIds.map((id) => `roadSegmentId=${id}`).join('&')
      : '';

  const sourceParams = sources.map((source) => `source=${source}`).join('&');
  const causeParams = causeId
    ? excludeCauseAndSubcause
      ? `&excludeCauseId=${causeId}`
      : `&causeId=${causeId}`
    : '';
  const subCauseParams = subCauseId
    ? excludeCauseAndSubcause
      ? `&excludeSubCauseId=${subCauseId}`
      : `&subCauseId=${subCauseId}`
    : '';
  const updatedFromParams = updatedFrom ? updatedFrom.toISOString() : dateString;
  const updatedToParams = updatedTo ? `&updatedBefore=${updatedTo.toISOString()}` : '';
  const sincroDispatchableParams = sincroDispatchable
    ? `&sincroDispatchable=${sincroDispatchable}`
    : '';

  const statusToParams = {
    [DenmStatusFilter.IN_PROGRESS]: { active: true, disabled: false },
    [DenmStatusFilter.DISABLED]: { disabled: true, active: true },
    [DenmStatusFilter.CLOSED]: { active: false },
  };

  let statusParams = '';
  if (status !== '') {
    const params = statusToParams[status];
    statusParams = Object.keys(params)
      .map((key) => `${key}=${params[key]}`)
      .join('&');
  }

  return {
    source: sourceParams,
    status: statusParams,
    roadSegments: roadSegments,
    causeId: causeParams,
    subCauseId: subCauseParams,
    updatedFrom: updatedFromParams,
    updatedTo: updatedToParams,
    sincroDispatchable: sincroDispatchableParams,
    page,
    limit,
  };
};

function* fetchDENMs({
  date,
  sources,
  status,
  roadSegmentIds,
  causeId,
  subCauseId,
  updatedFrom,
  updatedTo,
  sincroDispatchable,
  alwaysShowActiveEvents,
  excludeCauseAndSubcause,
  page,
  limit,
}: DENMListPayloadProps) {
  const payload: DENMListPayloadProps = {
    date,
    sources,
    status,
    causeId,
    subCauseId,
    updatedFrom,
    updatedTo,
    sincroDispatchable,
    alwaysShowActiveEvents,
    excludeCauseAndSubcause,
    page,
    limit,
  };

  const topologyType = getTopologyType();
  if (topologyType !== TopologyType.URBAN && roadSegmentIds && roadSegmentIds.length > 0) {
    payload.roadSegmentIds = roadSegmentIds;
  }

  const requestPayload = getDenmRequestPayload(payload);

  if (!requestPayload) {
    yield* put(denmsReducer.actions.removeAllDenms());
    return null;
  }

  return yield fetchAPI(apiRoute.getDENMs(requestPayload));
}

export function* getDENMEvent(payload: PayloadAction<GetDENMPayload>) {
  const { id } = payload.payload;
  try {
    const response = yield fetchAPI(apiRoute.getDENM(id));
    yield* put(denmsReducer.actions.denmResponse(response));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log('Error getDENMEvent >>>>>>>', e);
    yield* put(denmsReducer.actions.denmResponse(null));
  }
}

export function* getDENMEvents(action: PayloadAction<GetDENMListPaylaod>) {
  const fetchGeneration = yield* select(
    (state: { denms: { denmsFetchGeneration: number } }) => state.denms.denmsFetchGeneration,
  );

  const {
    roadSegmentIds,
    date,
    sources,
    causeId,
    subCauseId,
    updatedFrom,
    updatedTo,
    sincroDispatchable,
    alwaysShowActiveEvents,
    getAllData,
    status,
    excludeCauseAndSubcause,
    currentPage,
    itemsPerPage,
  } = action.payload;

  function* createPayload(
    sources: DenmSourceFilter[],
    roadSegmentIds?: string[],
    page?: number,
    limit?: number,
  ) {
    const payload: DENMListPayloadProps = {
      date,
      sources,
      status,
      causeId,
      subCauseId,
      updatedFrom,
      updatedTo,
      sincroDispatchable,
      alwaysShowActiveEvents,
      excludeCauseAndSubcause,
      page,
      limit,
    };

    const topologyType = getTopologyType();
    if (topologyType !== TopologyType.URBAN && roadSegmentIds && roadSegmentIds.length > 0) {
      payload.roadSegmentIds = roadSegmentIds;
    }

    return payload;
  }

  try {
    let resultsResponse = [];
    let totalResponse = 0;
    let page = 1;
    const limit = pageLimit;

    if (getAllData) {
      let response;
      do {
        response = yield* fetchDENMs(yield* createPayload(sources, roadSegmentIds, page, limit));
        resultsResponse = [...resultsResponse, ...response.results];
        page += 1;
      } while (response.total > resultsResponse.length);
    } else {
      const response = yield* fetchDENMs(
        yield* createPayload(sources, roadSegmentIds, currentPage, itemsPerPage),
      );
      if (response) {
        totalResponse = response.total;
        resultsResponse = response.results;
      }

      if (alwaysShowActiveEvents) {
        const activeResponse = yield* fetchDENMs({
          date,
          sources: Object.values(DenmSourceFilter),
          status: DenmStatusFilter.IN_PROGRESS,
          roadSegmentIds,
          updatedFrom: new Date(0),
          page: 1,
          limit: pageLimit,
        });

        if (activeResponse?.results?.length) {
          const denmById = new Map(resultsResponse.map((denm) => [denm.id, denm]));
          activeResponse.results.forEach((denm) => denmById.set(denm.id, denm));
          resultsResponse = Array.from(denmById.values());
          totalResponse = Math.max(totalResponse, resultsResponse.length);
        }
      }
    }

    const currentGeneration = yield* select(
      (state: { denms: { denmsFetchGeneration: number } }) => state.denms.denmsFetchGeneration,
    );
    if (fetchGeneration !== currentGeneration) {
      return;
    }

    if (getAllData) {
      yield* put(denmsReducer.actions.denmsAllDataResponse(resultsResponse));
    } else {
      yield* put(
        denmsReducer.actions.denmsResponse({ results: resultsResponse, total: totalResponse }),
      );
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log('Error getDENMEvents >>>>>>>', e);
  }
}

export function* statusTimer(action: PayloadAction<any>) {
  const pendingTime = 30000;
  const date = new Date();
  const utcDate = Date.UTC(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds(),
  );

  // let roadSegmentId = yield* select(selectedRoadSegmentIdSelector);
  // const initialDENMs = yield* select(denmsSelectorForSelectedRoadSegment(roadSegmentId));

  const timeDelay = differenceInMilliseconds(new Date(action.payload.expiresAt), new Date(utcDate));
  yield delay(timeDelay);
  // roadSegmentId = yield* select(selectedRoadSegmentIdSelector);

  // Correggo l'uso del selettore per evitare l'errore TypeError
  const selectedRoadSegmentIds = yield* select(selectedRoadSegmentIdsSelector);

  // Se non ci sono segmenti stradali selezionati, usa tutti i DENM filtrati
  // altrimenti usa solo quelli per i segmenti selezionati
  const currentDENMs =
    selectedRoadSegmentIds && selectedRoadSegmentIds.length > 0
      ? yield* select(denmsSelectorForSelectedRoadSegment(selectedRoadSegmentIds))
      : yield* select(denmsFilteredSelector);

  if (currentDENMs.find((d) => d.id === action.payload.id)) {
    yield put(
      denmsReducer.actions.denmUpsertOne({ ...action.payload, visibilityStatus: 'pending' }),
    );
    yield delay(pendingTime);
  }

  yield put(denmsReducer.actions.denmRemove(action.payload.id));
}

export function* createDENM(action: PayloadAction<CreateDENMPayload>) {
  yield put(appReducer.actions.dialogRequestStarted());
  // FIXME
  // const roads = yield* select(roadsSelector);
  let toastContent: ToastTitle;

  const params = action.payload;
  try {
    const result = yield fetchAPI(apiRoute.createDENM, {
      method: Method.POST,
      params: { ...params },
    });
    toastContent = {
      id: 'forms-denm-creation-success',
    };
    yield put(appReducer.actions.dialogRequestFinished(result));
  } catch (e) {
    toastContent = {
      id: 'forms-denm-creation-fail',
      args: { error: e?.packet?.message ?? 'Unknown error' },
    };
  } finally {
    yield put(appReducer.actions.removeMapElements(PointSelectionType.DENM_REFERENCE_POINT));
    yield put(appReducer.actions.removeMapElements(PointSelectionType.DENM_HISTORY));
    yield put(appReducer.actions.removeMapElements(PointSelectionType.DENM_TRACES));
    yield put(appReducer.actions.toastAdded(toastContent));
  }
}

export function* updateDENM(action: PayloadAction<UpdateDENMPayload>) {
  yield put(appReducer.actions.dialogRequestStarted());
  // FIXME
  // const roads = yield* select(roadsSelector);
  let toastContent: ToastTitle;

  try {
    const { id: denmId, denmPayload } = action.payload;
    const result = yield fetchAPI(apiRoute.updateDENM(denmId), {
      method: Method.PATCH,
      params: { ...denmPayload },
    });
    toastContent = {
      id: 'forms-denm-update-success',
    };
    yield put(appReducer.actions.dialogRequestFinished(result));
  } catch (e) {
    toastContent = {
      id: 'forms-denm-update-fail',
      args: { error: e?.packet?.message ?? 'Unknown error' },
    };
  } finally {
    yield put(appReducer.actions.removeMapElements(PointSelectionType.DENM_REFERENCE_POINT));
    yield put(appReducer.actions.removeMapElements(PointSelectionType.DENM_HISTORY));
    yield put(appReducer.actions.removeMapElements(PointSelectionType.DENM_TRACES));
    yield put(appReducer.actions.toastAdded(toastContent));
  }
}

export function* deleteDENM(action: PayloadAction<string>) {
  yield* put(appReducer.actions.dialogRequestStarted());

  try {
    const id = action.payload;
    yield fetchAPI(apiRoute.deleteDENM(id), { method: Method.DELETE });
    yield* put(appReducer.actions.denmDeleted({ result: { ok: true }, id }));
    yield* put(appReducer.actions.dialogRequestFinished({ ok: true }));
    yield put(denmsReducer.actions.denmRemove(id));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log('Error deleteDENM >>>>>>>', e);
  }
}

export function* deleteDENMs(action: PayloadAction<string[]>) {
  yield* put(appReducer.actions.dialogRequestStarted());

  try {
    const ids = action.payload;
    yield fetchAPI(apiRoute.deleteDENMs, { method: Method.POST, params: { eventIds: ids } });
    yield* put(appReducer.actions.denmDeleted({ result: { ok: true }, id: ids }));
    yield put(denmsReducer.actions.denmRemoveMany(ids));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log('Error deleteDENM >>>>>>>', e);
  }
}

export function* toggleDisableDENM(action: PayloadAction<DisableDENMPayload>) {
  yield* put(appReducer.actions.dialogRequestStarted());

  try {
    const { ids, disable } = action.payload;
    yield fetchAPI(apiRoute.disableDENM(String(disable)), {
      method: Method.POST,
      params: { eventIds: ids },
    });
    yield* put(appReducer.actions.denmsDisabled({ result: { ok: true }, ids }));
    yield put(denmsReducer.actions.denmRemoveMany(ids));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log('Error disableDENM >>>>>>>', e);
  }
}

function* causesRequest(_action: PayloadAction<void>) {
  try {
    const response = yield fetchAPI(apiRoute.causes);
    yield* put(denmsReducer.actions.causesResponse(response.results));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log('Error causesRequest >>>>>>>', e);
  }
}

function* subCausesRequest(_action: PayloadAction<void>) {
  try {
    const response = yield fetchAPI(apiRoute.subCauses);
    yield* put(denmsReducer.actions.subCausesResponse(response.results));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log('Error subCausesRequest >>>>>>>', e);
  }
}

function* subCausesByCauseRequest(action: PayloadAction<string>) {
  const causeId = action.payload;
  try {
    const response = yield fetchAPI(apiRoute.subCausesByCause(causeId));
    yield* put(denmsReducer.actions.subCausesByCauseResponse(response.results));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log('Error subCausesByCauseRequest >>>>>>>', e);
  }
}

function* optionalSubCausesByCauseRequest(action: PayloadAction<string>) {
  const causeId = action.payload;
  try {
    const response = yield fetchAPI(apiRoute.subCausesByCause(causeId));
    yield* put(denmsReducer.actions.optionalSubCausesByCauseResponse(response.results));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log('Error optionalSubCausesByCauseRequest >>>>>>>', e);
  }
}

// Motorway trace saga
interface MotorwayTracePayload {
  longitude: number;
  latitude: number;
  radius?: number;
}

function* fetchMotorwayTraceRequest(action: PayloadAction<MotorwayTracePayload>) {
  console.log('🔥 SAGA TRIGGERED! fetchMotorwayTraceRequest called');
  console.log('🔥 Action received:', action);
  try {
    const { longitude, latitude, radius } = action.payload;
    console.log('🌐 fetchMotorwayTraceRequest called with:', { longitude, latitude, radius });

    const radiusParam = radius ? `&radius=${radius}` : '';
    const topologyParam = process.env.TOPOLOGY_TYPE;
    const url = `geo/traces?longitude=${longitude}&latitude=${latitude}&topology=${topologyParam}${radiusParam}`;
    console.log('📡 Making API call to:', url);

    const response = yield fetchAPI(url);

    console.log('📥 API response received:', response);
    console.log('📥 Response type:', typeof response);
    console.log('📥 Response structure:', JSON.stringify(response, null, 2));

    if (
      response &&
      response.type === 'MultiLineString' &&
      response.coordinates &&
      Array.isArray(response.coordinates)
    ) {
      console.log('✅ Response is valid MultiLineString GeoJSON');
      console.log('✅ Number of line strings:', response.coordinates.length);

      // Process all line strings in the MultiLineString
      const allTracePoints: Coordinate[][] = [];

      response.coordinates.forEach((lineString: number[][], index: number) => {
        console.log(`✅ Processing line string ${index + 1}:`, lineString);

        if (lineString && Array.isArray(lineString) && lineString.length > 0) {
          // Convert the line string to coordinates format
          const tracePoints: Coordinate[] = lineString.map((coord: number[]) => [
            coord[0], // longitude
            coord[1], // latitude
          ]);
          console.log(`🗺️ Line string ${index + 1} converted to:`, tracePoints);
          allTracePoints.push(tracePoints);
        } else {
          console.log(`❌ Line string ${index + 1} has no valid coordinates`);
        }
      });

      console.log('🗺️ All trace points processed:', allTracePoints);
      console.log('🗺️ Total number of traces:', allTracePoints.length);

      if (allTracePoints.length > 0) {
        console.log('🎯 Dispatching motorway trace success with all traces:', allTracePoints);

        // Dispatch success action with all the trace points
        yield put(denmsReducer.actions.fetchMotorwayTraceSuccess(allTracePoints));

        console.log('✅ Dispatching success toast...');
        yield put(
          appReducer.actions.toastAdded({
            id: 'Motorway trace loaded successfully',
          }),
        );

        console.log('🎉 Motorway trace saga completed successfully!');
      } else {
        console.log('❌ No valid trace points found in any line string');
        yield put(denmsReducer.actions.fetchMotorwayTraceError('No valid trace points found'));
      }
    } else {
      console.log('❌ Invalid response format - expected MultiLineString GeoJSON:', response);
      yield put(
        denmsReducer.actions.fetchMotorwayTraceError(
          'Invalid response format - expected MultiLineString GeoJSON',
        ),
      );
    }
  } catch (error) {
    console.error('💥 Error in fetchMotorwayTraceRequest:', error);
    console.log('🚨 Dispatching error toast...');
    yield put(denmsReducer.actions.fetchMotorwayTraceError('Failed to fetch motorway trace'));
    yield put(
      appReducer.actions.toastAdded({
        id: 'Error loading motorway trace',
      }),
    );
  }
}

export const denmsSagas = createSliceSaga({
  name: 'denms',
  caseSagas: {
    denmsRequest: getDENMEvents,
    denmRequest: getDENMEvent,
    createDENM,
    updateDENM,
    deleteDENM,
    deleteDENMs,
    disableDENM: toggleDisableDENM,
    causesRequest,
    subCausesRequest,
    subCausesByCauseRequest,
    optionalSubCausesByCauseRequest,
    statusTimer,
    fetchMotorwayTraceRequest,
  },
  sagaType: SagaType.Watch,
});
